import React, { useState, useEffect, useMemo } from 'react';
import { Plus, RefreshCw, Trash2, X } from 'lucide-react';
import './PendingUpdatesPage.css';

interface Client {
  email: string;
  username: string;
  org_name: string;
  status: string;
  expires_at: string;
  has_shared_key: boolean;
  license_id: string;
}

interface PendingUpdate {
  _id: string;
  client_email: string;
  update_type: 'license_extension' | 'user_limits';
  status: 'pending' | 'fetched' | 'applied' | 'failed';
  days_to_add?: number;
  new_expires_at?: string;
  org_name?: string;
  max_admins?: number;
  max_viewers?: number;
  created_at: string;
  created_by: string;
  expires_at: string;
}

type StatusFilter = '' | 'pending' | 'fetched' | 'applied' | 'failed';

const STATUS_META: Record<string, { label: string; class: string }> = {
  pending: { label: 'Pending', class: 'pu-stat--pending' },
  fetched: { label: 'Fetched', class: 'pu-stat--fetched' },
  applied: { label: 'Applied', class: 'pu-stat--applied' },
  failed: { label: 'Failed', class: 'pu-stat--failed' },
};

const PendingUpdatesPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [updates, setUpdates] = useState<PendingUpdate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');

  const [selectedClient, setSelectedClient] = useState('');
  const [updateType, setUpdateType] = useState<'license_extension' | 'user_limits'>('license_extension');
  const [daysToAdd, setDaysToAdd] = useState(30);
  const [orgName, setOrgName] = useState('');
  const [maxAdmins, setMaxAdmins] = useState(2);
  const [maxViewers, setMaxViewers] = useState(5);
  const [creating, setCreating] = useState(false);

  const API_BASE = '/api/dashboard';

  const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchClients();
    fetchUpdates();
  }, []);

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 4000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_BASE}/clients/list`, { headers: getAuthHeaders() });
      const data = await response.json();
      if (data.success) setClients(data.clients);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };

  const fetchUpdates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/pending-updates`, { headers: getAuthHeaders() });
      const data = await response.json();
      if (data.success) {
        setUpdates(data.updates);
        setError(null);
      } else {
        setError(data.error);
      }
    } catch {
      setError('Failed to fetch pending updates');
    } finally {
      setLoading(false);
    }
  };

  const selectedClientObj = useMemo(
    () => clients.find((c) => c.email === selectedClient) ?? null,
    [clients, selectedClient]
  );

  const clientMap = useMemo(() => {
    const m = new Map<string, Client>();
    clients.forEach((c) => m.set(c.email, c));
    return m;
  }, [clients]);

  const stats = useMemo(
    () => ({
      pending: updates.filter((u) => u.status === 'pending').length,
      fetched: updates.filter((u) => u.status === 'fetched').length,
      applied: updates.filter((u) => u.status === 'applied').length,
      failed: updates.filter((u) => u.status === 'failed').length,
      total: updates.length,
    }),
    [updates]
  );

  const filteredUpdates = useMemo(
    () => (statusFilter ? updates.filter((u) => u.status === statusFilter) : updates),
    [updates, statusFilter]
  );

  const handleClientSelect = (email: string) => {
    setSelectedClient(email);
    const client = clients.find((c) => c.email === email);
    if (client) setOrgName(client.org_name !== 'Unknown' ? client.org_name : '');
  };

  const resetForm = () => {
    setSelectedClient('');
    setDaysToAdd(30);
    setOrgName('');
    setMaxAdmins(2);
    setMaxViewers(5);
    setUpdateType('license_extension');
  };

  const handleCreateUpdate = async () => {
    if (!selectedClient) {
      setError('Please select a client');
      return;
    }

    const payload: Record<string, unknown> = {
      client_email: selectedClient,
      update_type: updateType,
    };

    if (updateType === 'license_extension') {
      payload.days_to_add = daysToAdd;
    } else {
      payload.org_name = orgName || selectedClient;
      payload.max_admins = maxAdmins;
      payload.max_viewers = maxViewers;
    }

    try {
      setCreating(true);
      const response = await fetch(`${API_BASE}/pending-updates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMsg(`Update queued for ${selectedClient}`);
        setShowCreateModal(false);
        resetForm();
        fetchUpdates();
      } else {
        setError(data.error || 'Failed to create update');
      }
    } catch {
      setError('Failed to create update');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUpdate = async (updateId: string) => {
    if (!window.confirm('Delete this pending update?')) return;
    try {
      const response = await fetch(`${API_BASE}/pending-updates/${updateId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMsg('Update deleted');
        fetchUpdates();
      } else {
        setError(data.error || 'Failed to delete');
      }
    } catch {
      setError('Failed to delete update');
    }
  };

  const formatDate = (iso: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (iso: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const daysUntilExpiry = (expiresAt: string) => {
    if (!expiresAt) return null;
    return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  };

  const displayOrg = (u: PendingUpdate, client?: Client) => {
    const org = client?.org_name && client.org_name !== 'Unknown' ? client.org_name : u.org_name;
    return org?.trim() || '—';
  };

  const toggleStatusFilter = (key: StatusFilter) => {
    setStatusFilter((prev) => (prev === key ? '' : key));
  };

  return (
    <div className="pu-page">
      <div className="pu-stats">
        {(['pending', 'fetched', 'applied', 'failed'] as const).map((key) => (
          <button
            key={key}
            type="button"
            className={`pu-stat ${STATUS_META[key].class} ${statusFilter === key ? 'pu-stat--active' : ''}`}
            onClick={() => toggleStatusFilter(key)}
          >
            <span className="pu-stat-value">{(stats as Record<string, number>)[key]}</span>
            <span className="pu-stat-label">{STATUS_META[key].label}</span>
          </button>
        ))}
      </div>

      <div className="pu-toolbar">
        <span className="pu-toolbar-note">
          {stats.total} updates · applied on client login
        </span>
        <button type="button" className="pu-btn" onClick={fetchUpdates} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'pu-spin' : ''} />
          Refresh
        </button>
        <button type="button" className="pu-btn pu-btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={14} />
          New update
        </button>
      </div>

      {error && (
        <div className="pu-toast pu-toast-error">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}
      {successMsg && (
        <div className="pu-toast pu-toast-success">
          <span>{successMsg}</span>
          <button type="button" onClick={() => setSuccessMsg(null)} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}

      <div className="pu-table-panel">
        {loading && updates.length === 0 ? (
          <div className="pu-empty"><RefreshCw size={20} className="pu-spin" /> Loading…</div>
        ) : filteredUpdates.length === 0 ? (
          <div className="pu-empty">
            <p>{statusFilter ? `No ${statusFilter} updates` : 'No pending updates yet'}</p>
            {!statusFilter && (
              <button type="button" className="pu-btn pu-btn-primary" onClick={() => setShowCreateModal(true)}>
                <Plus size={14} /> Create update
              </button>
            )}
          </div>
        ) : (
          <div className="pu-table-scroll">
            <table className="pu-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Type</th>
                  <th>Details</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredUpdates.map((u) => {
                  const client = clientMap.get(u.client_email);
                  const org = displayOrg(u, client);
                  return (
                    <tr key={u._id}>
                      <td>
                        <span className="pu-org-name">{org}</span>
                        <span className="pu-org-email">{u.client_email}</span>
                      </td>
                      <td>
                        <span className={`pu-type pu-type--${u.update_type}`}>
                          {u.update_type === 'license_extension' ? 'Extension' : 'Limits'}
                        </span>
                      </td>
                      <td>
                        {u.update_type === 'license_extension' ? (
                          <span className="pu-detail-text">
                            +{u.days_to_add} days · exp {formatDate(u.new_expires_at || '')}
                          </span>
                        ) : (
                          <span className="pu-detail-text">
                            {u.max_admins} admins / {u.max_viewers} viewers
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`pu-status pu-status--${u.status}`}>
                          {u.status}
                        </span>
                      </td>
                      <td>
                        <span className="pu-date">{formatDateTime(u.created_at)}</span>
                        <span className="pu-by">by {u.created_by || 'admin'}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="pu-icon-delete"
                          onClick={() => handleDeleteUpdate(u._id)}
                          disabled={u.status === 'applied'}
                          title={u.status === 'applied' ? 'Already applied' : 'Delete'}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="pu-overlay" onClick={() => { setShowCreateModal(false); resetForm(); }}>
          <div className="pu-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pu-modal-header">
              <h2>New pending update</h2>
              <button type="button" className="pu-modal-close" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                <X size={16} />
              </button>
            </div>

            <div className="pu-form-section">
              <label className="pu-label">Client</label>
              <div className="pu-client-grid">
                {clients.length === 0 ? (
                  <div className="pu-no-clients">No active clients</div>
                ) : (
                  clients.map((c) => {
                    const days = daysUntilExpiry(c.expires_at);
                    const isSelected = selectedClient === c.email;
                    const org = c.org_name !== 'Unknown' ? c.org_name : c.email;
                    return (
                      <button
                        key={c.email}
                        type="button"
                        className={`pu-client-card ${isSelected ? 'pu-client-card-selected' : ''}`}
                        onClick={() => handleClientSelect(c.email)}
                      >
                        <div>
                          <div className="pu-client-card-org">{org}</div>
                          <div className="pu-client-card-email">{c.email}</div>
                        </div>
                        <span className={`pu-client-card-expiry ${days !== null && days <= 7 ? 'pu-expiry-warn' : ''}`}>
                          {days !== null ? (days > 0 ? `${days}d left` : 'Expired') : '—'}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="pu-form-section">
              <label className="pu-label">Update type</label>
              <div className="pu-type-toggle">
                <button
                  type="button"
                  className={`pu-type-btn ${updateType === 'license_extension' ? 'pu-type-btn-active' : ''}`}
                  onClick={() => setUpdateType('license_extension')}
                >
                  License extension
                </button>
                <button
                  type="button"
                  className={`pu-type-btn ${updateType === 'user_limits' ? 'pu-type-btn-active' : ''}`}
                  onClick={() => setUpdateType('user_limits')}
                >
                  User limits
                </button>
              </div>
            </div>

            {updateType === 'license_extension' ? (
              <div className="pu-form-section">
                <label className="pu-label">Days to add</label>
                <input
                  className="pu-input"
                  type="number"
                  value={daysToAdd}
                  onChange={(e) => setDaysToAdd(parseInt(e.target.value, 10) || 0)}
                  min={1}
                  max={365}
                />
                {selectedClientObj?.expires_at && (
                  <p className="pu-hint">Current expiry: {formatDate(selectedClientObj.expires_at)}</p>
                )}
              </div>
            ) : (
              <>
                <div className="pu-form-section">
                  <label className="pu-label">Organization name</label>
                  <input className="pu-input" type="text" value={orgName} onChange={(e) => setOrgName(e.target.value)} />
                </div>
                <div className="pu-form-row">
                  <div className="pu-form-section">
                    <label className="pu-label">Max admins</label>
                    <input className="pu-input" type="number" value={maxAdmins} onChange={(e) => setMaxAdmins(parseInt(e.target.value, 10) || 0)} min={1} />
                  </div>
                  <div className="pu-form-section">
                    <label className="pu-label">Max viewers</label>
                    <input className="pu-input" type="number" value={maxViewers} onChange={(e) => setMaxViewers(parseInt(e.target.value, 10) || 0)} min={0} />
                  </div>
                </div>
              </>
            )}

            <div className="pu-modal-actions">
              <button type="button" className="pu-btn pu-btn-ghost" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                Cancel
              </button>
              <button type="button" className="pu-btn pu-btn-primary" onClick={handleCreateUpdate} disabled={!selectedClient || creating}>
                {creating ? 'Creating…' : 'Create update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingUpdatesPage;
